import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Form, ListGroup, Spinner } from 'react-bootstrap';
import api from '../../services/api';

const DEBOUNCE_MS = 320;

/** Стабильная ссылка: иначе default `searchExtras = {}` в параметрах — новый объект на каждом рендере и срыв debounce. */
const EMPTY_SEARCH_EXTRAS = Object.freeze({});

function formatStoreLine(s) {
  const parts = [];
  if (s.store_id != null && String(s.store_id).trim() !== '') parts.push(String(s.store_id).trim());
  if (s.name) parts.push(s.name);
  return parts.length ? parts.join(' — ') : '—';
}

/**
 * Поиск магазинов через GET /admin/stores/ (superuser). Подсказки по подстроке name / store_id / email.
 * @param {'id'|'store_id'} valueField — что передавать в onFilterValueChange при выборе строки
 */
export default function AdminStoreSearchCombobox({
  filterValue,
  onFilterValueChange,
  valueField = 'id',
  /** Вызывается при выборе строки (объект Store с сервера) и при сбросе (null). */
  onStoreSelected = null,
  searchExtras,
  limit = 60,
  placeholder = 'Название, ID магазина, email…',
  disabled = false,
  className = '',
  size,
  loadingText = 'Поиск…',
  noMatchesText = 'No matches',
  /** После выбора строки очистить поле (удобно при множественном выборе магазинов родителем). */
  clearInputAfterPick = false,
  'aria-label': ariaLabel,
}) {
  const inputRef = useRef(null);
  const blurCloseTimer = useRef(null);
  const searchExtrasRef = useRef(searchExtras ?? EMPTY_SEARCH_EXTRAS);
  searchExtrasRef.current = searchExtras ?? EMPTY_SEARCH_EXTRAS;
  const fetchSeqRef = useRef(0);
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [suggestError, setSuggestError] = useState('');

  useEffect(() => {
    if (filterValue == null || filterValue === '') {
      setInputValue('');
    }
  }, [filterValue]);

  const fetchSuggestions = useCallback(
    async (q) => {
      const seq = ++fetchSeqRef.current;
      const query = q.trim();
      setSuggestLoading(true);
      setSuggestError('');
      try {
        const data = await api.getAdminStoresSearch(query, limit, searchExtrasRef.current);
        if (seq !== fetchSeqRef.current) return;
        setSuggestions(Array.isArray(data) ? data : []);
      } catch (err) {
        if (seq !== fetchSeqRef.current) return;
        setSuggestions([]);
        setSuggestError(err.message || 'Ошибка поиска');
      } finally {
        if (seq === fetchSeqRef.current) {
          setSuggestLoading(false);
        }
      }
    },
    [limit],
  );

  useEffect(() => {
    if (disabled) return undefined;
    const h = setTimeout(() => fetchSuggestions(inputValue), DEBOUNCE_MS);
    return () => clearTimeout(h);
  }, [inputValue, fetchSuggestions, disabled]);

  const handleInputChange = (e) => {
    const v = e.target.value;
    setInputValue(v);
    setSuggestOpen(true);
    if (filterValue) {
      onFilterValueChange('');
    }
  };

  const handleFocus = () => {
    setSuggestOpen(true);
    if (blurCloseTimer.current) {
      clearTimeout(blurCloseTimer.current);
      blurCloseTimer.current = null;
    }
  };

  const handleBlur = () => {
    blurCloseTimer.current = setTimeout(() => setSuggestOpen(false), 200);
  };

  const pickValue = (s) => {
    if (valueField === 'store_id') {
      return s.store_id != null ? String(s.store_id) : '';
    }
    return s.id != null ? String(s.id) : '';
  };

  const selectStore = (s) => {
    if (blurCloseTimer.current) {
      clearTimeout(blurCloseTimer.current);
      blurCloseTimer.current = null;
    }
    const v = pickValue(s);
    onFilterValueChange(v);
    setSuggestOpen(false);
    setSuggestions([]);
    if (typeof onStoreSelected === 'function') {
      onStoreSelected(s);
    }
    if (clearInputAfterPick) {
      setInputValue('');
    } else {
      setInputValue(formatStoreLine(s));
    }
  };

  const clearStore = () => {
    if (blurCloseTimer.current) {
      clearTimeout(blurCloseTimer.current);
      blurCloseTimer.current = null;
    }
    onFilterValueChange('');
    setInputValue('');
    setSuggestOpen(true);
    fetchSuggestions('');
    if (typeof onStoreSelected === 'function') {
      onStoreSelected(null);
    }
    inputRef.current?.focus();
  };

  const showList =
    suggestOpen &&
    !disabled &&
    (suggestLoading || suggestError || suggestions.length > 0 || inputValue.trim() !== '');

  return (
    <div className={`position-relative ${className}`}>
      <div className="d-flex gap-1 align-items-stretch">
        <Form.Control
          ref={inputRef}
          type="search"
          size={size}
          autoComplete="off"
          disabled={disabled}
          placeholder={placeholder}
          aria-label={ariaLabel || placeholder}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
        {filterValue ? (
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm px-2 flex-shrink-0"
            onMouseDown={(e) => e.preventDefault()}
            onClick={clearStore}
            aria-label="Сбросить магазин"
          >
            ×
          </button>
        ) : null}
      </div>
      {suggestError ? <div className="text-danger small mt-1">{suggestError}</div> : null}
      {showList ? (
        <ListGroup
          className="position-absolute w-100 shadow border rounded mt-1"
          style={{ zIndex: 1050, maxHeight: 240, overflowY: 'auto' }}
        >
          {suggestLoading ? (
            <ListGroup.Item className="text-muted small py-2">
              <Spinner animation="border" size="sm" className="me-2" />
              {loadingText}
            </ListGroup.Item>
          ) : null}
          {!suggestLoading && suggestions.length === 0 && !suggestError ? (
            <ListGroup.Item className="text-muted small py-2">{noMatchesText}</ListGroup.Item>
          ) : null}
          {!suggestLoading &&
            suggestions.map((s) => (
              <ListGroup.Item
                key={s.id}
                action
                className="small py-2"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => selectStore(s)}
              >
                <div className="fw-semibold text-truncate">{s.name || '—'}</div>
                <div className="text-muted font-monospace text-truncate">{s.store_id}</div>
                {s.user_email ? (
                  <div className="text-muted text-truncate" style={{ fontSize: '0.8rem' }}>
                    {s.user_email}
                  </div>
                ) : null}
              </ListGroup.Item>
            ))}
        </ListGroup>
      ) : null}
    </div>
  );
}
